import React from 'react';
import { IconType } from 'react-icons';
import * as bi from 'react-icons/bi';
import * as fa from 'react-icons/fa';
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { IconColor } from '../config/colorSchemes';
import { ICON_SIZE_MAP, IconSize } from '../constants/sizes';

const colorMap: Record<IconColor, string> = {
  light: 'var(--scheme1-text)',
  dark: 'var(--scheme2-text)',
  whatsapp: 'var(--color-whatsapp)',
  instagram: 'var(--color-instagram)',
};

/**
 * Icon map combining Boxicons and Font Awesome icons
 * Created once at module level to prevent recreation on every render
 */
const ICON_MAP: Record<string, IconType> = { ...bi, ...fa };

const SpanStyled = styled(motion.span)<{ $colour?: IconColor; $size?: IconSize }>`
  align-items: center;
  display: flex;
  color: ${({ $colour = 'light' }: { $colour?: IconColor }) => colorMap[$colour]};
  font-size: ${({ $size = 'medium' }: { $size?: IconSize }) => ICON_SIZE_MAP[$size]};
  justify-content: center;
  line-height: 1; /* Ensure proper vertical alignment */
  width: ${({ $size = 'medium' }: { $size?: IconSize }) => ICON_SIZE_MAP[$size]};
  height: ${({ $size = 'medium' }: { $size?: IconSize }) => ICON_SIZE_MAP[$size]};
`;

interface IconProps {
  name: string;
  alt?: string;
  size?: IconSize;
  colour?: IconColor;
  className?: string;
  decorative?: boolean;
}

const Icon: React.FC<IconProps> = ({
  name,
  alt = '',
  size,
  colour,
  className,
  decorative = true,
}) => {
  const IconComponent = ICON_MAP[name];
  if (!IconComponent) {
    console.error('🚀 ~ Icon.tsx ~ Icon not found:', name);
    return <SpanStyled $size={size} $colour={colour} aria-hidden="true" />;
  }

  return (
    <SpanStyled
      $size={size}
      $colour={colour}
      whileHover={{ scale: 1.05 }}
      aria-hidden={decorative}
      role={decorative ? undefined : 'img'}
      aria-label={!decorative ? alt : undefined}
    >
      <IconComponent className={className} aria-hidden="true" focusable="false" />
    </SpanStyled>
  );
};

export default Icon;
